import { MouseEventHandler, ReactNode } from "react";
import NextLink from "next/link";
import { twMerge } from "tailwind-merge";
import { ButtonSize, ButtonVariant, getButtonClassNames } from "../button";
import { ExternalLinkIcon } from "../../icons";
import { cn } from "../../utils/class-names";

/**
 * TODO: integrate `action` styles into Link (and set up `inline` variant?)
 */
type LinkVariant = "default" | ButtonVariant;

interface LinkProps extends UnstyledLinkProps {
  variant?: LinkVariant;
  size?: ButtonSize;
}

export const getLinkClassNames = (
  variant: LinkVariant,
  size: ButtonSize
): string => {
  const buttonVariant = variant === "default" ? "link" : variant;

  return getButtonClassNames(buttonVariant, size);
};

export const Link = ({
  href,
  variant = "default",
  size = "normal",
  className = "",
  external = false,
  onClick,
  children,
  "data-cy": dataCy,
}: LinkProps): JSX.Element => {
  return (
    <PlainLink
      href={href}
      data-cy={dataCy}
      className={twMerge(getLinkClassNames(variant, size), className)}
      onClick={onClick}
      external={external}
    >
      {children}
    </PlainLink>
  );
};

export function PlainLink({
  className,
  children,
  ...rest
}: UnstyledLinkProps): JSX.Element {
  return (
    <UnstyledLink
      className={cn("text-blue-300 hover:text-blue-200", className)}
      {...rest}
    >
      {children}
    </UnstyledLink>
  );
}

interface UnstyledLinkProps {
  href: string;
  className?: string;
  external?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  "data-cy"?: string;
  children?: ReactNode;
}

export const UnstyledLink = ({
  href,
  className = "",
  external = false,
  onClick,
  children,
  "data-cy": dataCy,
}: UnstyledLinkProps): JSX.Element => {
  return (
    <NextLink
      href={href}
      data-cy={dataCy}
      className={className}
      onClick={onClick}
      passHref={external}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </NextLink>
  );
};

export interface ExternalLinkProps {
  href: string;
  size: ButtonSize;
  className?: string;
  variant?: LinkVariant;
  children?: ReactNode;
}

/* stopping propagation, so that any parent links (like on the markets page) would not get triggered */
export const ExternalLink = ({
  href,
  size,
  children = "Learn more",
  className,
  variant,
}: ExternalLinkProps): JSX.Element => {
  return (
    <Link
      variant={variant}
      href={href}
      external
      onClick={(event) => event.stopPropagation()}
      size={size}
      className={className}
    >
      <span className="flex items-center gap-1">
        {children}
        <ExternalLinkIcon className={iconClassMap[size]} />
      </span>
    </Link>
  );
};

const iconClassMap: Record<ButtonSize, string> = {
  small: "w-4 h-4",
  normal: "w-5 h-5",
  big: "w-6 h-6",
};
